import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {useCallback, useMemo, useEffect} from 'react'
import {useParams} from 'react-router-dom'
import {SEARCH_PARAMS} from '../constants/search-params'
import {useSearchParams} from '@github-ui/use-navigate'

type UrlParams = {
  owner: string
  repo: string
  number: string
  viewId: string
  type: string
}

export const useRouteInfo = () => {
  const {owner, repo, number, viewId} = useParams<UrlParams>()
  const [searchParams, setSearchParams] = useSearchParams()

  const setSidePanelItemIdentifier = useCallback(
    (identifier: ItemIdentifier | null) => {
      identifier
        ? searchParams.set(SEARCH_PARAMS.issue, `${identifier.owner}|${identifier.repo}|${identifier.number}`)
        : searchParams.delete(SEARCH_PARAMS.issue)

      setSearchParams(searchParams)
    },
    [searchParams, setSearchParams],
  )

  // Ensure that on page load, we have the correct search params
  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search)

    const nwoReference = urlSearchParams.get(SEARCH_PARAMS.issue)
    if (!nwoReference) return

    setSearchParams(urlSearchParams)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const sidePanelItemIdentifier = useMemo((): ItemIdentifier | undefined => {
    const nwoReference = searchParams.get(SEARCH_PARAMS.issue)
    if (!nwoReference) return

    const [itemOwner, itemRepo, itemNumber] = nwoReference.split('|')
    if (!(itemOwner && itemRepo && itemNumber)) return

    const issueNumber = isNaN(parseInt(itemNumber)) ? undefined : parseInt(itemNumber)

    if (!issueNumber) return

    return {
      number: issueNumber,
      owner: itemOwner,
      repo: itemRepo,
      type: 'Issue',
    }
  }, [searchParams])

  const sidePanelItemURL = sidePanelItemIdentifier
    ? `/${sidePanelItemIdentifier.owner}/${sidePanelItemIdentifier.repo}/issues/${sidePanelItemIdentifier.number}`
    : ''

  const itemIdentifier = useMemo(() => {
    let parsedNumber = number ? parseInt(number, 10) : undefined
    if (parsedNumber && parsedNumber < 1) {
      parsedNumber = undefined
    }
    return owner && repo && parsedNumber ? ({owner, repo, number: parsedNumber} as ItemIdentifier) : undefined
  }, [number, owner, repo])

  return {itemIdentifier, viewId, sidePanelItemIdentifier, setSidePanelItemIdentifier, sidePanelItemURL}
}
